import './lang/i18n';

import { Button, ConfigProvider, Result, Space, Spin } from 'antd';
import { ErrorBoundary } from 'react-error-boundary';
import { Suspense, lazy } from 'react';

import useGetAuthRoutes from '@/routes/auth-routes';

import theme from '../theme/default.json';
import { languageMap } from './utils';

import { AuthProvider } from '@joyu/auth/dist/esm/context/auth-context-provider';
import {
  AuthedProjectsProvider,
  useProjects,
} from '@joyu/auth/dist/esm/context/authed-projects-context-provider';
import { UserInfoProvider } from '@joyu/auth';
import { useTranslation } from 'react-i18next';
import { LanguageCode } from './types/enum';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { RoutePath } from './routes/enum';

dayjs.locale('zh-cn');

const ResetPassword = lazy(() => import('@/pages/reset-password'));
const McpAuth = lazy(() => import('@/pages/mcp-auth'));

/** 不需要鉴权即可访问的路径前缀 */
const PUBLIC_PATHS = ['/reset-password', '/mcp-auth'];

const isPublicPath = () => PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

function AuthedRoutes() {
  const { authRoutes } = useGetAuthRoutes();

  return <Suspense fallback={<>...</>}>{authRoutes}</Suspense>;
}

function AuthedProject() {
  const { localSelectedProject } = useProjects();
  return (
    <>
      {localSelectedProject.code && (
        <AuthProvider>
          <AuthedRoutes />
        </AuthProvider>
      )}
      {!localSelectedProject.code && <div style={{ fontSize: 80 }}>门户网站</div>}
    </>
  );
}

function App() {
  const { i18n } = useTranslation();

  // reset-password 是公开页面，不走鉴权体系
  if (isPublicPath()) {
    const publicPage = window.location.pathname.startsWith('/mcp-auth') ? (
      <McpAuth />
    ) : (
      <ResetPassword />
    );
    return (
      <ConfigProvider locale={languageMap[i18n.language as LanguageCode || LanguageCode.ZhCn].antd} theme={theme}>
        <Suspense fallback={<Spin spinning />}>
          {publicPage}
        </Suspense>
      </ConfigProvider>
    );
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        console.log(error.message);
        // 在重新发版后
        if (error.message.includes('Failed to fetch dynamically imported module')) {
          (window.location).reload();
          return (
            <Spin spinning>
              <div style={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}></div>
            </Spin>
          );
        }

        return (
          <div style={{ paddingTop: '80px' }}>
            <Result
              status="500"
              title="出错啦"
              subTitle="当前页面出现了预期之外的错误，请联系管理员或重试"
              extra={
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      const location = window.location;
                      location.href = `${location.origin}/${RoutePath.GUIDE}`;
                    }}
                  >
                    前往工具箱
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      const location = window.location;
                      location.reload();
                    }}
                  >
                    刷新
                  </Button>
                </Space>
              }
            />
          </div>
        );
      }}
    >
      {/* 默认使用中文包，需要切换国际化时请自行配置 */}
      <ConfigProvider locale={languageMap[i18n.language as LanguageCode || LanguageCode.ZhCn].antd} theme={theme}>
        <UserInfoProvider env={import.meta.env.VITE_APP_ENV} appCode="joy_dev_platform">
          <AuthedProjectsProvider defaultProjectCode="default">
            <AuthedProject />
          </AuthedProjectsProvider>
        </UserInfoProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
